// architecture.md §4.2: renders every finding generically from the Finding JSON's own fields —
// no per-check-id branching here, so a brand-new rule shipped by the backend renders correctly
// with zero widget changes (architecture.md §2.2's whole point of the rule package).
//
// Findings are grouped by (ruleId, urlId) via @apc/shared-types' groupFindings before rendering
// — one rule firing many times on one page (e.g. 18 separate "invisible ad unit" findings) is
// one problem, not 18 near-identical cards. Found live on seo-head.de and confirmed with the
// user/product as a real UX bug, not intended behavior — see groupFindings' own doc comment for
// why grouping is per-page rather than domain-wide.
import { describeFindingInstance, groupFindings, type FindingGroup } from "@apc/shared-types";
import type { Finding } from "./types.js";

function escapeHtml(value: string): string {
  const div = document.createElement("div");
  div.textContent = value;
  return div.innerHTML;
}

const LABEL_CLASS: Record<Finding["label"], string> = {
  "Verstoß": "apc-label-violation",
  "auffällig": "apc-label-notable",
  "Hinweis": "apc-label-info",
};

const MAX_INSTANCES_SHOWN = 5;

function renderInstanceToggle(group: FindingGroup): string {
  if (group.instanceCount <= 1) return "";
  const shown = group.instances.slice(0, MAX_INSTANCES_SHOWN);
  const remaining = group.instanceCount - shown.length;
  return `
    <details class="apc-finding-instances">
      <summary>Show all ${group.instanceCount} locations</summary>
      <ul>
        ${shown.map((instance) => `<li><code>${escapeHtml(describeFindingInstance(instance.rawValue))}</code></li>`).join("")}
        ${remaining > 0 ? `<li>+${remaining} more</li>` : ""}
      </ul>
    </details>
  `;
}

function renderFindingGroup(group: FindingGroup): string {
  const representativeScreenshot = group.instances.find((i) => i.screenshotUrl)?.screenshotUrl ?? null;
  return `
    <article class="apc-finding ${LABEL_CLASS[group.label]}" data-rule-id="${escapeHtml(group.ruleId)}">
      <header class="apc-finding-header">
        <span class="apc-finding-label">${escapeHtml(group.label)}</span>
        <h4 class="apc-finding-title">${escapeHtml(group.title)}</h4>
        ${group.instanceCount > 1 ? `<span class="apc-finding-count">Found in ${group.instanceCount} places</span>` : ""}
      </header>
      <p class="apc-finding-explanation">${escapeHtml(group.explanation)}</p>
      <p class="apc-finding-fix"><strong>Fix:</strong> ${escapeHtml(group.fix)}</p>
      ${representativeScreenshot ? `<img class="apc-finding-screenshot" src="${escapeHtml(representativeScreenshot)}" alt="Annotated screenshot for ${escapeHtml(group.title)}" loading="lazy" />` : ""}
      ${renderInstanceToggle(group)}
      ${group.adpressoSolves ? `<p class="apc-adpresso-note">AdPresso can fix this for you.</p>` : ""}
    </article>
  `;
}

export function renderScore(domainScore: number | null): string {
  if (domainScore === null) return "";
  return `<div class="apc-score" role="img" aria-label="Score ${domainScore} of 100">${domainScore}<span class="apc-score-max">/100</span></div>`;
}

export function renderReport(domain: string, domainScore: number | null, findings: Finding[]): string {
  const officialCount = findings.filter((f) => f.isOfficialViolation).length;
  const recommendationCount = findings.length - officialCount;
  const groups = groupFindings(findings);
  const sorted = [...groups].sort((a, b) => Number(b.isOfficialViolation) - Number(a.isOfficialViolation));

  return `
    <div class="apc-report">
      <div class="apc-report-header">
        ${renderScore(domainScore)}
        <div class="apc-report-summary">
          <h3>${escapeHtml(domain)}</h3>
          <p>${officialCount} official violation${officialCount === 1 ? "" : "s"} · ${recommendationCount} recommendation${recommendationCount === 1 ? "" : "s"}</p>
        </div>
      </div>
      <div class="apc-findings-list">
        ${sorted.length > 0 ? sorted.map(renderFindingGroup).join("") : `<p class="apc-no-findings">No findings on the scanned pages.</p>`}
      </div>
      <p class="apc-disclaimer">This report is advisory only and does not guarantee AdSense approval or account safety. "AdSense" is a trademark of Google LLC; AdPresso is not affiliated with Google.</p>
    </div>
  `;
}
